// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField, useUserInputValue } from '@tectonics/prs/user-input-values';
import { InputMask } from '@tectonics/components/input-mask';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useTranslation } from 'react-i18next';

import { TextField } from '../../../../atoms';
import { GenericValidationMessage } from '../generic/generic-validation-message';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import { useClearPhoneNumberPrefill } from '@tectonics/prs/ui/use-clear-phone-number-prefill';

export function FieldSouthAfricanPhoneNumber(): Node {
  const { t } = useTranslation('opened-page');
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [selectedEWallet] = useUserInputValue('selectedEWallet');
  const [value, handleChange] = useUserInputAsTextField('southAfricanPhoneNumber');

  const validationError = useUserInputValidationError('southAfricanPhoneNumber');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('telephone');

  useClearPhoneNumberPrefill('southAfricanPhoneNumber');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  const isCapitecPay = selectedEWallet === 'capitec-pay'
    && selectedPaymentMethod === 'ewallet';

  const fieldLabel = isCapitecPay
    ? t('volcanes.customerForm.phoneNumber.capitecLabel')
    : t('volcanes.customerForm.phoneNumber.label');

  return (
    <div data-test-name="customer-phone-number">
      <label htmlFor={inputId}>{fieldLabel}</label>

      <TextField
        as={InputMask}
        mask={isCapitecPay ? SOUTH_AFRICA_CAPITEC_MASK : SOUTH_AFRICA_MASK}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        id={inputId}
        placeholder={t('volcanes.customerForm.phoneNumber.placeholder')}
        onChange={handleChange}
        readOnly={submitting}
        type="tel"
        value={value}
        name="customer-phone-number"
      />

      {formHasBeenSent && validationError && (
        <GenericValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

const SOUTH_AFRICA_MASK = [
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/, /\d/, /\d/,
];

const SOUTH_AFRICA_CAPITEC_MASK = [
  '0',
  /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/,
  /\d/, /\d/, /\d/,
];
